import { useCallback, useEffect, useState } from "react";
import { Delete } from "lucide-react";

export function PinPad({ title, subtitle, onComplete, error, testPrefix = "pin" }) {
  const [pin, setPin] = useState("");
  const [shake, setShake] = useState(false);
  const press = useCallback((d) => setPin((p) => (p.length < 4 ? p + d : p)), []);
  const back = () => setPin((p) => p.slice(0, -1));

  useEffect(() => {
    if (pin.length !== 4) return;
    Promise.resolve(onComplete(pin)).then((ok) => {
      if (ok === false) { setShake(true); setTimeout(() => setShake(false), 400); }
      setPin("");
    });
  }, [pin]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const h = (e) => {
      if (/^\d$/.test(e.key)) press(e.key);
      if (e.key === "Backspace") back();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [press]);

  return (
    <div className="flex flex-col items-center gap-6" data-testid={`${testPrefix}-pad`}>
      <div className="text-center">
        <h2 className="font-heading text-xl sm:text-2xl font-semibold tracking-tight">{title}</h2>
        {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
      </div>
      <div className={`flex gap-4 ${shake ? "sv-shake" : ""}`} data-testid={`${testPrefix}-dots`}>
        {[0, 1, 2, 3].map((i) => (
          <span key={i} className={`h-4 w-4 rounded-full border-2 transition-colors duration-150 ${i < pin.length ? "bg-primary border-primary" : "border-muted-foreground/50"}`} />
        ))}
      </div>
      <p className="h-4 text-xs text-destructive font-mono" data-testid={`${testPrefix}-error`}>{error}</p>
      <div className="grid grid-cols-3 gap-3">
        {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((d) => <Key key={d} d={d} onClick={() => press(d)} tp={testPrefix} />)}
        <span />
        <Key d="0" onClick={() => press("0")} tp={testPrefix} />
        <button type="button" onClick={back} data-testid={`${testPrefix}-backspace`} aria-label="Backspace"
          className="h-16 w-16 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors">
          <Delete className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}

function Key({ d, onClick, tp }) {
  return (
    <button type="button" onClick={onClick} data-testid={`${tp}-key-${d}`}
      className="h-16 w-16 rounded-full border bg-card font-heading text-2xl font-medium hover:border-primary hover:text-primary active:scale-95 transition-[transform,color,border-color] duration-150">
      {d}
    </button>
  );
}
